import { useEffect, useState, type ChangeEvent } from 'react';
import MfaSettings from '../components/MfaSettings';
import Spinner from '../components/Spinner';
import {
  fetchConnectedApps,
  disconnectApp,
  fetchMyProfile,
  updateMyProfile,
  uploadAvatar,
  type ConnectedApp,
  type UserProfile,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { authEnabled } from '../auth/auth';
import { scopeLabel } from '../lib/oauthScopes';
import { displayNameFallback } from '../lib/userDisplay';

type Tab = 'profile' | 'apps' | 'security';

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ja-JP');
  } catch {
    return iso;
  }
}

export default function MyPage() {
  const { user, ready, login } = useAuth();
  const [tab, setTab] = useState<Tab>('profile');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [apps, setApps] = useState<ConnectedApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const [nicknameInput, setNicknameInput] = useState('');
  const [savingNickname, setSavingNickname] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    // 連携アプリ一覧のような一部エンドポイントは backend 未反映で 404/CORS になり得るため、
    // Promise.all だとプロフィールタブまで巻き添えで空になる。allSettled で個別に扱う。
    Promise.allSettled([fetchMyProfile(), fetchConnectedApps()])
      .then(([prRes, aRes]) => {
        if (prRes.status === 'fulfilled') {
          setProfile(prRes.value);
          setNicknameInput(prRes.value?.nickname ?? '');
        }
        if (aRes.status === 'fulfilled') setApps(aRes.value);
        const errs = [prRes, aRes]
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r) => String(r.reason));
        if (errs.length > 0) setError(errs.join('\n'));
      })
      .finally(() => setLoading(false));
  }, [user]);

  const handleDisconnect = async (clientId: string) => {
    if (!window.confirm('この連携を解除しますか？アプリからのアクセスは即座に無効になります。')) return;
    setDisconnectingId(clientId);
    setError('');
    try {
      await disconnectApp(clientId);
      setApps((prev) => prev.filter((a) => a.clientId !== clientId));
    } catch (e) {
      setError(String(e));
    } finally {
      setDisconnectingId(null);
    }
  };

  const handleIconChange = async (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = ''; // 同じファイルを連続選択できるようにリセット
    if (!file) return;
    setUploadingIcon(true);
    try {
      const iconKey = await uploadAvatar(file);
      const updated = await updateMyProfile({ iconKey });
      setProfile(updated);
    } catch (e) {
      window.alert(String(e));
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleRemoveIcon = async () => {
    setUploadingIcon(true);
    try {
      const updated = await updateMyProfile({ iconKey: null });
      setProfile(updated);
    } catch (e) {
      window.alert(String(e));
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleSaveNickname = async () => {
    const trimmed = nicknameInput.trim();
    if (trimmed.length < 1 || trimmed.length > 32) return;
    setSavingNickname(true);
    try {
      const updated = await updateMyProfile({ nickname: trimmed });
      setProfile(updated);
      setNicknameInput(updated.nickname);
      window.alert('保存しました');
    } catch (e) {
      window.alert(String(e));
    } finally {
      setSavingNickname(false);
    }
  };

  if (!ready) {
    return (
      <div className="container">
        <Spinner />
      </div>
    );
  }

  if (authEnabled && !user) {
    return (
      <div className="container">
        <div className="section-title">
          <h2>マイページ</h2>
        </div>
        <div className="notice" style={{ marginBottom: 20 }}>
          マイページの表示にはログインが必要です。
        </div>
        <button className="btn btn--primary" onClick={login}>
          ログイン / 新規登録
        </button>
      </div>
    );
  }

  // 初回登録前（プロフィール未作成）はニックネームが未保存のため、アイコンだけ先に登録することはできない
  // （updateMyProfile({ iconKey }) 単体では初回作成時の nickname 必須チェックに引っかかる）。
  const iconUploadDisabled = uploadingIcon || !profile;
  const nicknameTrimmed = nicknameInput.trim();

  return (
    <div className="container">
      <div className="section-title">
        <h2>マイページ</h2>
        {user && <span className="stat">@{user.name}</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button
          className={`btn${tab === 'profile' ? ' btn--primary' : ''}`}
          onClick={() => setTab('profile')}
        >
          プロフィール
        </button>
        <button
          className={`btn${tab === 'apps' ? ' btn--primary' : ''}`}
          onClick={() => setTab('apps')}
        >
          連携アプリ（{apps.length}）
        </button>
        <button
          className={`btn${tab === 'security' ? ' btn--primary' : ''}`}
          onClick={() => setTab('security')}
        >
          セキュリティ
        </button>
      </div>

      {loading ? (
        // 読み込み中はタブ本体を表示しない（未取得の profile / apps でちらつくのを防ぐ）。
        <Spinner />
      ) : (
        <>
          {error && <p className="viewer__error">{error}</p>}

          {tab === 'profile' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 420 }}>
              <h3 style={{ margin: 0 }}>プロフィール設定</h3>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {profile?.iconUrl ? (
                  <img
                    src={profile.iconUrl}
                    alt=""
                    style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 72,
                      height: 72,
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: 'var(--color-accent-soft)',
                      color: 'var(--color-accent-dark)',
                      fontSize: 28,
                      fontWeight: 700,
                    }}
                  >
                    {displayNameFallback(user?.email, user?.sub).slice(0, 1).toUpperCase()}
                  </span>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleIconChange}
                    disabled={iconUploadDisabled}
                  />
                  {uploadingIcon && (
                    <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>アップロード中…</span>
                  )}
                  {!profile && !uploadingIcon && (
                    <p className="notice" style={{ margin: 0 }}>
                      先にニックネームを入力して保存してください
                    </p>
                  )}
                  {profile?.iconUrl && !uploadingIcon && (
                    <button className="btn" type="button" onClick={handleRemoveIcon}>
                      アイコンを削除
                    </button>
                  )}
                </div>
              </div>

              <div className="field">
                <label htmlFor="nickname">ニックネーム</label>
                <input
                  id="nickname"
                  type="text"
                  maxLength={32}
                  value={nicknameInput}
                  onChange={(e) => setNicknameInput(e.target.value)}
                />
              </div>
              <div>
                <button
                  className="btn btn--primary"
                  type="button"
                  disabled={savingNickname || nicknameTrimmed.length < 1 || nicknameTrimmed.length > 32}
                  onClick={handleSaveNickname}
                >
                  {savingNickname ? '保存中…' : '保存'}
                </button>
              </div>
            </div>
          )}

          {tab === 'apps' && apps.length === 0 && <p>連携中のアプリはありません。</p>}

          {tab === 'apps' && apps.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {apps.map((app) => (
                <div key={app.clientId} className="connected-app">
                  <div className="connected-app__name">{app.appName || '(名称未設定のアプリ)'}</div>
                  {app.appDescription && <p style={{ margin: 0 }}>{app.appDescription}</p>}

                  <div>
                    <p style={{ margin: '4px 0', fontWeight: 700 }}>許可した権限</p>
                    {app.scope.length === 0 ? (
                      <p style={{ margin: 0 }}>権限の要求はありません。</p>
                    ) : (
                      <ul style={{ margin: 0 }}>
                        {app.scope.map((s) => (
                          <li key={s}>{scopeLabel(s)}</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <p className="connected-app__meta">
                    連携日時: {formatDateTime(app.createdAt)} ／ 最終更新: {formatDateTime(app.updatedAt)}
                  </p>

                  <div>
                    <button
                      className="btn"
                      onClick={() => handleDisconnect(app.clientId)}
                      disabled={disconnectingId === app.clientId}
                      type="button"
                    >
                      {disconnectingId === app.clientId ? '解除中…' : '連携解除'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'security' && <MfaSettings />}
        </>
      )}
    </div>
  );
}
